<template>
  <div class="content-wrapper">
    <section class="content-header">
      <h1>Nudge Console</h1>
      <ol class="breadcrumb">
        <li><a href="#"><i class="fa fa-dashboard"></i> Home</a></li>
        <li class="active">Dashboard</li>
      </ol>
    </section>

    <section class="content nc-page">
      <!-- Two levels of filtering, deliberately separated. Customer and journey are a
           SCOPE — PRD §3.3 puts them at the top because they decide which programme you're
           looking at, and they apply to every tab and every count. Search and date are a
           REFINEMENT within the chosen tab, so they sit below it. -->
      <div class="nc-surface nd-scope">
        <div class="nd-scope-field">
          <label for="nd-customer">Customer</label>
          <nv-select id="nd-customer" v-model="customerId" class="nc-input" @change="reload">
            <option value="">All customers</option>
            <option v-for="client in customers" :key="client.id" :value="client.id">{{ client.name }}</option>
          </nv-select>
        </div>
        <div class="nd-scope-field">
          <label for="nd-journey">Journey</label>
          <nv-select id="nd-journey" v-model="journeyId" class="nc-input" @change="reload">
            <option value="">All journeys</option>
            <option v-for="journey in journeys" :key="journey.id" :value="journey.id">{{ journey.name }}</option>
          </nv-select>
        </div>
        <span v-if="scopeActive" class="nd-scope-note">
          <i class="fa fa-filter"></i> Scoping every tab, count and card below.
        </span>
      </div>

      <div class="nc-toolbar">
        <div class="nc-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="nc-tab"
            :class="{ 'is-active': activeTab === tab.key }"
            @click="selectTab(tab.key)"
          >
            {{ tab.label }}
            <span class="nc-tab-count">{{ tabCounts[tab.key] }}</span>
          </button>
        </div>

        <div class="nd-toolbar-right">
          <!-- Same segmented-control language as the tabs, so the page has one vocabulary
               for "pick one of these". Defaults to list: at four cards across, a long
               campaign list is far quicker to scan as rows. -->
          <div class="nc-tabs nd-view-switch" role="group" aria-label="View">
            <button
              class="nc-tab"
              :class="{ 'is-active': viewMode === 'list' }"
              title="List view"
              @click="setViewMode('list')"
            ><i class="fa fa-list"></i> List</button>
            <button
              class="nc-tab"
              :class="{ 'is-active': viewMode === 'box' }"
              title="Box view"
              @click="setViewMode('box')"
            ><i class="fa fa-th-large"></i> Box</button>
          </div>

          <router-link class="nc-btn nc-btn-primary" to="/nudge-console/create-campaign">
            <i class="fa fa-plus"></i> Create Campaign
          </router-link>
        </div>
      </div>

      <div v-if="!showingDrafts" class="nc-surface nc-filters">
        <div class="nc-field nc-field-grow">
          <label for="nd-search">Search</label>
          <input
            id="nd-search"
            v-model="searchText"
            type="text"
            class="nc-input"
            placeholder="Campaign name — press Enter"
            @keyup.enter="reload"
          />
        </div>
        <div class="nc-field">
          <label for="nd-type">Type</label>
          <nv-select id="nd-type" v-model="campaignType" class="nc-input" @change="reload">
            <option v-for="option in campaignTypeOptions" :key="option.label" :value="option.value">
              {{ option.label }}
            </option>
          </nv-select>
        </div>
        <div class="nc-field">
          <label for="nd-from">Created from</label>
          <input id="nd-from" v-model="fromDt" type="date" class="nc-input" @change="reload" />
        </div>
        <div class="nc-field">
          <label for="nd-to">Created to</label>
          <input id="nd-to" v-model="toDt" type="date" class="nc-input" @change="reload" />
        </div>
        <button v-if="hasFilters" class="nc-btn nc-btn-ghost" @click="clearFilters">Clear</button>
      </div>

      <!-- ── Drafts: wizard chat sessions, not campaign rows ──────────────────
           A draft has no opportunity record yet, so it can't reuse the campaign card
           body below. Source is the user's own ai_console_chat sessions. -->
      <div v-if="showingDrafts">
        <div v-if="!loading && !drafts.length" class="nc-surface nc-empty">
          <div class="nc-empty-icon"><i class="fa fa-file-o"></i></div>
          <div class="nc-empty-title">No drafts in progress</div>
          <div class="nc-empty-text">
            The chats auto-save every 60 seconds, so an unfinished campaign or template shows up here.
            <router-link to="/nudge-console/create-campaign">Start a campaign</router-link> or
            <router-link to="/nudge-template/ai-create">create a template</router-link>.
          </div>
        </div>

        <div v-else-if="isList" class="nc-surface nd-table-wrap">
          <table class="nd-table">
            <thead>
              <tr>
                <th>Draft</th>
                <th>Nudge type</th>
                <th>Journey</th>
                <th>Step</th>
                <th>Last edited</th>
                <th class="nd-col-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="draft in drafts" :key="draft.id">
                <td><span class="nd-cell-name">{{ draftName(draft) }}</span></td>
                <td>{{ draftType(draft) }}</td>
                <td>{{ draftJourney(draft) }}</td>
                <td>{{ draftStep(draft) }}</td>
                <td>{{ draftEdited(draft) }}</td>
                <td class="nd-col-actions">
                  <div class="nd-row-actions">
                    <router-link
                      class="nc-btn nc-btn-sm nc-btn-ghost"
                      :to="draftResumeLink(draft)"
                    >
                      Resume →
                    </router-link>
                    <button class="nc-btn nc-btn-sm nc-btn-danger" title="Discard draft" @click="confirmDiscardDraft(draft)">
                      <i class="fa fa-trash-o"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="nd-cards">
          <div v-for="draft in drafts" :key="draft.id" class="nc-surface nd-card">
            <div class="nd-card-head">
              <div>
                <div class="nd-card-name">{{ draftName(draft) }}</div>
                <div class="nd-card-meta">{{ draftStep(draft) }}</div>
              </div>
              <span class="nc-badge" :class="isTemplateChat(draft) ? 'nc-badge-teal' : 'nc-badge-warning'">{{ isTemplateChat(draft) ? 'Template draft' : 'Campaign draft' }}</span>
            </div>
            <div class="nd-card-rows">
              <div class="nd-card-row">
                <span class="nd-card-row-label">Nudge type</span>
                <span class="nd-card-row-value">{{ draftType(draft) }}</span>
              </div>
              <div class="nd-card-row">
                <span class="nd-card-row-label">Journey</span>
                <span class="nd-card-row-value">{{ draftJourney(draft) }}</span>
              </div>
              <div class="nd-card-row">
                <span class="nd-card-row-label">Last edited</span>
                <span class="nd-card-row-value">{{ draftEdited(draft) }}</span>
              </div>
            </div>
            <div class="nd-card-actions">
              <router-link
                class="nc-btn nc-btn-sm nc-btn-primary"
                :to="draftResumeLink(draft)"
              >
                Resume →
              </router-link>
              <span class="nd-spacer"></span>
              <button class="nc-btn nc-btn-sm nc-btn-danger" title="Discard draft" @click="confirmDiscardDraft(draft)">
                <i class="fa fa-trash-o"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Campaigns ────────────────────────────────────────────────────── -->
      <template v-else>
        <div v-if="!loading && !campaigns.length" class="nc-surface nc-empty">
          <div class="nc-empty-icon"><i class="fa fa-inbox"></i></div>
          <div class="nc-empty-title">No {{ activeTabLabel }}campaigns to show</div>
          <div class="nc-empty-text">
            Nothing matches the current scope and filters.
            <router-link to="/nudge-console/create-campaign">Create a campaign</router-link>
            to get started.
          </div>
        </div>

        <div v-else-if="isList" class="nc-surface nd-table-wrap">
          <table class="nd-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Type</th>
                <th>Status</th>
                <th>Nudge Console</th>
                <th>Trigger</th>
                <th class="nd-col-num">Fires</th>
                <th>Last fired</th>
                <th>Created</th>
                <th class="nd-col-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="campaign in campaigns" :key="campaign.id" :class="{ 'nd-row-highlight': isHighlighted(campaign) }">
                <td>
                  <span class="nd-cell-name">{{ campaign.opportunity_name }}</span>
                  <span class="nd-cell-sub">
                    {{ campaign.country_name || '—' }} · {{ campaign.customer_name || 'No customer' }}
                  </span>
                </td>
                <td>
                  <span class="nd-type" :class="campaignTypeOf(campaign).className">
                    <i :class="campaignTypeOf(campaign).icon"></i>
                    {{ campaignTypeOf(campaign).label }}
                  </span>
                </td>
                <td>
                  <span class="nc-badge" :class="statusBadgeClass(campaign)" :title="statusTitle(campaign)">{{ statusLabel(campaign) }}</span>
                </td>
                <td>
                  <span class="nc-badge" :class="sourceBadgeClass(campaign)">{{ sourceLabel(campaign) }}</span>
                </td>
                <td>
                  {{ triggerText(campaign) }}
                  <span class="nd-cell-sub">{{ sessionText(campaign) }}</span>
                </td>
                <td class="nd-col-num">{{ firesText(campaign) }}</td>
                <td>{{ lastFiredText(campaign) }}</td>
                <td>{{ createdText(campaign) }}</td>
                <td class="nd-col-actions">
                  <div class="nd-row-actions">
                    <label
                      v-if="!showingDeleted && statusAction(campaign)"
                      class="nd-toggle"
                      :title="statusAction(campaign).label + ' this campaign'"
                    >
                      <input
                        type="checkbox"
                        :checked="campaign.opportunity_status === activeStatus"
                        @change="confirmStatusChange(campaign)"
                      />
                      <span class="nd-toggle-track"><span class="nd-toggle-knob"></span></span>
                    </label>
                    <router-link
                      class="nc-btn nc-btn-sm nc-btn-quiet"
                      title="View analytics"
                      :to="{ path: '/nudge-console/analytics', query: { campaignId: campaign.id } }"
                    >
                      <i class="fa fa-bar-chart"></i>
                    </router-link>
                    <!-- Hidden on the Deleted tab for the same reason the toggle and delete
                         are: that tab is read-only, and a deleted campaign has nothing to
                         edit until it's restored. -->
                    <router-link
                      v-if="!showingDeleted"
                      class="nc-btn nc-btn-sm nc-btn-quiet"
                      title="Edit campaign"
                      :to="{ path: '/nudge-console/edit-campaign', query: { campaignId: campaign.id } }"
                    >
                      <i class="fa fa-pencil"></i>
                    </router-link>
                    <button
                      v-if="!showingDeleted && !campaign.is_in_session && campaign.group_list && campaign.group_list.length"
                      class="nc-btn nc-btn-sm nc-btn-quiet"
                      title="Send test"
                      @click="testNudgeClick(campaign)"
                    >
                      <i class="fa fa-paper-plane-o"></i>
                    </button>
                    <button
                      v-if="!showingDeleted"
                      class="nc-btn nc-btn-sm nc-btn-danger"
                      title="Delete campaign"
                      @click="confirmDelete(campaign)"
                    >
                      <i class="fa fa-trash-o"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="nd-cards">
          <div v-for="campaign in campaigns" :key="campaign.id" class="nc-surface nd-card" :class="{ 'nd-row-highlight': isHighlighted(campaign) }">
            <div class="nd-card-head">
              <div>
                <div class="nd-card-name">{{ campaign.opportunity_name }}</div>
                <div class="nd-card-meta">
                  {{ campaign.country_name || '—' }} · {{ campaign.customer_name || 'No customer' }}
                </div>
              </div>
              <div class="nd-card-badges">
                <span class="nc-badge" :class="statusBadgeClass(campaign)" :title="statusTitle(campaign)">{{ statusLabel(campaign) }}</span>
                <span class="nd-type" :class="campaignTypeOf(campaign).className">
                  <i :class="campaignTypeOf(campaign).icon"></i>
                  {{ campaignTypeOf(campaign).label }}
                </span>
              </div>
            </div>

            <div v-if="gateOf(campaign) && campaign.template_gate_reason" class="nd-card-gate">
              <i class="fa fa-exclamation-triangle"></i> {{ campaign.template_gate_reason }}
            </div>

            <div class="nd-card-rows">
              <div class="nd-card-row">
                <span class="nd-card-row-label">Nudge Console campaign</span>
                <span class="nd-card-row-value">
                  <span class="nc-badge" :class="sourceBadgeClass(campaign)">{{ sourceLabel(campaign) }}</span>
                </span>
              </div>
              <div class="nd-card-row">
                <span class="nd-card-row-label">Trigger</span>
                <span class="nd-card-row-value">{{ triggerText(campaign) }}</span>
              </div>
              <div class="nd-card-row">
                <span class="nd-card-row-label">Window</span>
                <span class="nd-card-row-value">{{ sessionText(campaign) }}</span>
              </div>
              <div class="nd-card-row">
                <span class="nd-card-row-label">Content</span>
                <span class="nd-card-row-value">{{ contentText(campaign) }}</span>
              </div>
              <div class="nd-card-row">
                <span class="nd-card-row-label">Fires to date</span>
                <span class="nd-card-row-value">{{ firesText(campaign) }}</span>
              </div>
              <div class="nd-card-row">
                <span class="nd-card-row-label">Last fired</span>
                <span class="nd-card-row-value">{{ lastFiredText(campaign) }}</span>
              </div>
              <div class="nd-card-row">
                <span class="nd-card-row-label">Created</span>
                <span class="nd-card-row-value">{{ createdText(campaign) }}</span>
              </div>
            </div>

            <div class="nd-card-actions">
              <!-- PRD §3.3.2 asks for a toggle rather than a button: a triggered campaign
                   is a rule that's either on or off, and a toggle shows its current state
                   as well as the action. -->
              <label
                v-if="!showingDeleted && statusAction(campaign)"
                class="nd-toggle"
                :title="statusAction(campaign).label + ' this campaign'"
              >
                <input
                  type="checkbox"
                  :checked="campaign.opportunity_status === activeStatus"
                  @change="confirmStatusChange(campaign)"
                />
                <span class="nd-toggle-track"><span class="nd-toggle-knob"></span></span>
                <span class="nd-toggle-label">
                  {{ campaign.opportunity_status === activeStatus ? 'Active' : (gateOf(campaign) ? gateOf(campaign).action + '?' : 'Paused') }}
                </span>
              </label>
              <router-link
                class="nc-btn nc-btn-sm nc-btn-quiet"
                :to="{ path: '/nudge-console/analytics', query: { campaignId: campaign.id } }"
              >
                <i class="fa fa-bar-chart"></i> Analytics
              </router-link>
              <router-link
                v-if="!showingDeleted"
                class="nc-btn nc-btn-sm nc-btn-quiet"
                :to="{ path: '/nudge-console/edit-campaign', query: { campaignId: campaign.id } }"
              >
                <i class="fa fa-pencil"></i> Edit
              </router-link>
              <span class="nd-spacer"></span>
              <button
                v-if="!showingDeleted && !campaign.is_in_session && campaign.group_list && campaign.group_list.length"
                class="nc-btn nc-btn-sm nc-btn-quiet"
                title="Send test"
                @click="testNudgeClick(campaign)"
              >
                <i class="fa fa-paper-plane-o"></i> Send Test
              </button>
              <button
                v-if="!showingDeleted"
                class="nc-btn nc-btn-sm nc-btn-danger"
                title="Delete campaign"
                @click="confirmDelete(campaign)"
              >
                <i class="fa fa-trash-o"></i>
              </button>
            </div>
          </div>
        </div>

        <div v-if="pageCount > 1" class="nd-pager-wrap">
          <span class="nd-pager-summary">{{ rangeSummary }}</span>
          <paginate
            :page-count="pageCount"
            :page-range="5"
            :margin-pages="1"
            :force-page="page"
            :click-handler="goToPage"
            :prev-text="'Prev'"
            :next-text="'Next'"
            :container-class="'nc-pagination'"
            :page-class="'nc-pagination-item'"
            :page-link-class="'nc-pagination-link'"
            :prev-class="'nc-pagination-item'"
            :next-class="'nc-pagination-item'"
            :prev-link-class="'nc-pagination-link nc-pagination-nav'"
            :next-link-class="'nc-pagination-link nc-pagination-nav'"
            :break-view-class="'nc-pagination-item nc-pagination-break'"
            :break-view-link-class="'nc-pagination-link'"
            :active-class="'nc-pagination-item-active'"
            :disabled-class="'nc-pagination-item-disabled'"
          />
        </div>
      </template>
    </section>

    <form @submit.prevent="sendTestNudge" method="post">
    <div class="modal-backdrop" v-if="audienceSelectionVisible" id="nudgeConsoleUserSelection">
      <div class="modal modal-md modal-user-selection">
          <div class="test-nudge-container">
            <slot name="body">
              <div class="form-horizontal">

                <div class="row">
                  <div class="form-group col-xs-12 col-sm-12 col-md-12">
                    <label class="control-label modal-label" for="search">Select Group:</label>
                    &nbsp;&nbsp;
                    <div style="display: flex; flex-flow: wrap;">
                      <div v-for="group in selectedCampaign.group_list" :key="group.group_name" style="width: 233px;">
                        <label class="radio-inline campaign-group-details" style="display: flex; flex-direction: column; margin-bottom: 10px; padding-left: 20px;"
                            v-bind:class="{'reengagement-group-disabled': !group.msg_template_id }">
                          <input type="radio" v-model="selectedGroup" :value="group" :disabled="!group.msg_template_id" />
                          <div>{{group.group_name}}</div>
                          <div>Nudge: <span class="campaign-details-text">{{group.msg_template_name ? group.msg_template_name : '-'}}</span></div>
                          <div>Split Percent: <span class="campaign-details-text">{{group.split_percent || group.split_percent === 0 ? group.split_percent + '%' : '-'}}</span></div>
                        </label>
                      </div>
                    </div>
                    <span v-if="submittedTestNudge && !$v.selectedGroup.required" class="text-red pull-right" style="margin-right: 20px">Please select a group.</span>
                  </div>
                </div>

                <div class="row">
                  <div class="form-group col-xs-12 col-sm-12 col-md-12">
                    <label class="control-label modal-label" for="search">Select test user(s):</label>
                    <label class="hint-text">Only onboarded users appear here. To include your account in a test, make sure you're currently onboarded to the correct country.</label>
                    &nbsp;&nbsp;
                    <div class="users-checkbox-container">
                      <div v-for="user in userList" :key="user.account_id">
                        <label class="checkbox-inline col-xs-6 col-sm-6 col-md-5 col-lg-4">
                          <input type="checkbox" v-model="selectedUsers" :value="user"/>{{user.account_name}}
                        </label>
                      </div>
                      <div v-if="!userList.length">There are no users </div>
                    </div>
                    <span v-if="submittedTestNudge && !$v.selectedUsers.required" class="text-red pull-right" style="margin-right: 20px">Please select users to send message.</span>
                  </div>
                </div>

              </div>
            </slot>
          </div>
          <div class="modal-footer" style="margin-top: auto; padding: 0px">
            <div class="form-group pull-right">
              <div class="col-sm-12">
                <button type="button" class="btn btn-primary" @click="closeAudienceSelection">Cancel</button>
                <button type="submit" class="btn btn-primary">Send Test</button>
              </div>
            </div>
          </div>
      </div>
    </div>
    </form>

    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br />Please wait...</BlockUI>
  </div>
</template>

<script>
import Paginate from 'vuejs-paginate'
import api from '../../../utils/api'
import { formatDate } from '../../../utils/utilities'
import { required } from 'vuelidate/lib/validators'
import {
  CAMPAIGN_STATUS,
  STATUS_LABELS,
  STATUS_BADGE_CLASSES,
  STATUS_ACTION,
  TEMPLATE_GATES,
  DASHBOARD_TABS,
  CHAT_PURPOSE,
  TEMPLATE_CHAT_PURPOSE,
  CAMPAIGN_TYPE_OPTIONS,
  describeCampaignType,
  describeDraftStep,
  describeDraftName,
  isNudgeConsoleCampaign,
  describeTrigger,
  describeSession,
  describeSplit,
  safeInt
} from './constants'

// The statistics service doesn't reliably hand `is_completed` back as a real JS boolean
// (observed as the string "false"/"true", not just `false`/`true`) — since any non-empty
// string is truthy, `!chat.is_completed` was always false for a string value regardless
// of what it said, so the Drafts tab filtered out every real draft and always read 0.
// Compare explicitly instead of relying on JS truthiness.
const isChatIncomplete = chat => {
  const v = chat && chat.is_completed
  return v !== true && v !== 'true' && v !== 1 && v !== '1' && v !== 't'
}

// Divisible by every column count the box grid uses (4 / 3 / 2 / 1), so a full page always
// fills whole rows instead of leaving orphans and a gap. See NudgeDashboard.css. List view
// doesn't care, but it shares the page size so switching views doesn't reshuffle paging.
const PAGE_SIZE = 12

// Per-viewer convenience only, so localStorage is the right home for it — it never needs to
// reach another device or be read back by anything else. Reads and writes are guarded
// because a private window or blocked site data makes the accessor throw.
const VIEW_MODE_KEY = 'nudgeConsole.dashboardView'

function storedViewMode () {
  try {
    const value = localStorage.getItem(VIEW_MODE_KEY)
    return value === 'box' ? 'box' : 'list'
  } catch (err) {
    return 'list'
  }
}

export default {
  name: 'nudge-dashboard',
  components: { paginate: Paginate },
  validations: {
    selectedUsers: { required },
    selectedGroup: { required }
  },
  data () {
    return {
      loading: false,
      campaigns: [],
      drafts: [],
      // Keyed by campaign id: { fires, lastFiredAt } from /nudge-campaign-activity.
      activity: {},
      customers: [],
      journeys: [],
      totalCount: 0,
      tabs: DASHBOARD_TABS,
      activeTab: 'all',
      activeStatus: CAMPAIGN_STATUS.active,
      // Each tab's own total. The list only ever holds one page, so the tab badges need
      // their own counts rather than anything derived from `campaigns`.
      tabCounts: DASHBOARD_TABS.reduce((acc, tab) => ({ ...acc, [tab.key]: 0 }), {}),
      page: 1,
      customerId: '',
      journeyId: '',
      campaignType: '',
      campaignTypeOptions: CAMPAIGN_TYPE_OPTIONS,
      viewMode: storedViewMode(),
      fromDt: '',
      toDt: '',
      searchText: '',
      // Set from ?opportunityId= when arriving via the "View in Dashboard" chat link, so the
      // just-created/saved campaign is easy to spot in an otherwise unfiltered list.
      highlightId: null,
      // Send Test — same flow as Database Marketing's opportunity list (opportunityList.vue).
      audienceSelectionVisible: false,
      selectedCampaign: null,
      selectedUsers: [],
      selectedGroup: null,
      userList: [],
      submittedTestNudge: false
    }
  },
  computed: {
    pageCount () {
      return Math.ceil(this.totalCount / PAGE_SIZE)
    },
    currentTab () {
      return this.tabs.find(tab => tab.key === this.activeTab) || this.tabs[0]
    },
    // Reads as "No active campaigns to show" / "No campaigns to show" for the All tab.
    activeTabLabel () {
      return this.currentTab.key === 'all' ? '' : `${this.currentTab.label.toLowerCase()} `
    },
    hasFilters () {
      return Boolean(this.searchText || this.fromDt || this.toDt || this.campaignType)
    },
    isList () {
      return this.viewMode === 'list'
    },
    scopeActive () {
      return Boolean(this.customerId || this.journeyId)
    },
    rangeSummary () {
      const first = ((this.page - 1) * PAGE_SIZE) + 1
      const last = Math.min(this.page * PAGE_SIZE, this.totalCount)
      return `${first}–${last} of ${this.totalCount}`
    },
    // Deleted campaigns are read-only here — reactivating one means restoring it, which
    // updateOpportunityStatus can't express (deletion is itself a status change).
    showingDeleted () {
      return Boolean(this.currentTab.isDeleted)
    },
    showingDrafts () {
      return Boolean(this.currentTab.isWizardDraft)
    }
  },
  mounted () {
    this.highlightId = this.$route.query.opportunityId || null
    // ?tab=drafts etc., e.g. the chat's "Go to Draft Chats" link.
    if (this.tabs.some(tab => tab.key === this.$route.query.tab)) this.activeTab = this.$route.query.tab
    this.loadScopeOptions()
    this.reload()
  },
  methods: {
    gateOf (campaign) {
      return (campaign.opportunity_status !== this.activeStatus && TEMPLATE_GATES[campaign.template_gate_status]) || null
    },
    statusLabel (campaign) {
      const gate = this.gateOf(campaign)
      return gate ? gate.label : (STATUS_LABELS[campaign.opportunity_status] || 'Unknown')
    },
    statusBadgeClass (campaign) {
      const gate = this.gateOf(campaign)
      return gate ? gate.badge : (STATUS_BADGE_CLASSES[campaign.opportunity_status] || 'nc-badge-neutral')
    },
    statusTitle (campaign) {
      return this.gateOf(campaign) ? (campaign.template_gate_reason || '') : ''
    },
    statusAction (campaign) {
      const action = STATUS_ACTION[campaign.opportunity_status] || null
      const gate = this.gateOf(campaign)
      return action && gate ? Object.assign({}, action, { label: gate.action }) : action
    },
    isHighlighted (campaign) {
      return this.highlightId != null && String(campaign.id) === String(this.highlightId)
    },
    setViewMode (mode) {
      if (this.viewMode === mode) return
      this.viewMode = mode
      try {
        localStorage.setItem(VIEW_MODE_KEY, mode)
      } catch (err) { /* preference only — the view still switches */ }
    },
    campaignTypeOf (campaign) {
      return describeCampaignType(campaign)
    },
    sourceLabel (campaign) {
      return isNudgeConsoleCampaign(campaign) ? 'Yes' : 'No'
    },
    sourceBadgeClass (campaign) {
      return isNudgeConsoleCampaign(campaign) ? 'nc-badge-teal' : 'nc-badge-neutral'
    },
    triggerText (campaign) {
      return describeTrigger(campaign)
    },
    sessionText (campaign) {
      return describeSession(campaign)
    },
    createdText (campaign) {
      return formatDate(campaign.created, 'd/M/Y') || '—'
    },
    // Group count doubles as the A/B indicator: the always-on flow writes one group for a
    // single version and two when a Variant B was added (buildMessageGroups).
    contentText (campaign) {
      const groups = campaign.group_list || []
      if (!groups.length) return 'No content groups'
      const split = describeSplit(campaign)
      return split ? `A/B test · ${split}` : 'Single version'
    },
    // Activity loads after the cards, so an un-fetched campaign reads "—" rather than a
    // misleading 0.
    firesText (campaign) {
      const row = this.activity[String(campaign.id)]
      if (!row) return '—'
      return row.fires ? row.fires.toLocaleString() : 'Not fired yet'
    },
    lastFiredText (campaign) {
      const row = this.activity[String(campaign.id)]
      if (!row || !row.lastFiredAt) return '—'
      return this.relativeTime(row.lastFiredAt)
    },
    // "4 min ago" / "3 days ago" — the Dashboard's job is to show that a rule is alive, and
    // a bare timestamp makes the reader do that arithmetic.
    relativeTime (value) {
      const then = new Date(value).getTime()
      if (Number.isNaN(then)) return '—'
      const minutes = Math.floor((Date.now() - then) / 60000)
      if (minutes < 1) return 'just now'
      if (minutes < 60) return `${minutes} min ago`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
      const days = Math.floor(hours / 24)
      if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
      return formatDate(value, 'd/M/Y') || '—'
    },

    draftName (draft) {
      return describeDraftName(draft.summary)
    },
    draftStep (draft) {
      return describeDraftStep(draft.summary)
    },
    isTemplateChat (draft) {
      return draft.purpose === TEMPLATE_CHAT_PURPOSE
    },
    // Template drafts (AI Nudge Creation) resume on their own page.
    draftResumeLink (draft) {
      return { path: this.isTemplateChat(draft) ? '/nudge-template/ai-create' : '/nudge-console/create-campaign', query: { chatId: draft.id } }
    },
    draftType (draft) {
      if (this.isTemplateChat(draft)) return 'WhatsApp template (AI Nudge Creation)'
      const type = draft.summary && draft.summary.nudgeType
      if (type === 'always-on') return 'Always-on triggered'
      if (type === 'batch') return 'Batch out-of-session'
      if (type === 'in-session') return 'In-session nudge'
      return 'Not chosen yet'
    },
    draftJourney (draft) {
      const context = (draft.summary && draft.summary.context) || {}
      return (context.journey && context.journey.name) || '—'
    },
    draftEdited (draft) {
      return this.relativeTime(draft.updated_at || draft.created_at)
    },

    buildQueryParams (tab) {
      const params = {
        skip: 0,
        limit: PAGE_SIZE,
        searchText: (this.searchText || '').replace(/'/g, "''"),
        fromDt: this.fromDt || '',
        toDt: this.toDt || '',
        isDeleted: Boolean(tab.isDeleted)
      }
      // getOpportunityList ignores `status` entirely when isDeleted is set, and treats any
      // present `status` key as a filter — so it has to be left off for the All tab.
      if (tab.status !== null && tab.status !== undefined && !tab.isDeleted) params.status = tab.status
      if (this.customerId) params.customerId = this.customerId
      if (this.journeyId) params.journeyId = this.journeyId
      // Shared with fetchTabCounts, so the tab badges count the same population the grid
      // shows — a Type filter that left the counts alone would read as a bug.
      if (this.campaignType) params.campaignType = this.campaignType
      return params
    },

    selectTab (key) {
      if (this.activeTab === key) return
      this.activeTab = key
      this.page = 1
      if (this.showingDrafts) this.fetchDrafts()
      else this.fetchCampaigns()
    },

    // Filter changes have to reset paging — otherwise a narrower filter leaves you on a
    // page number that no longer exists and the list comes back empty.
    reload () {
      if (this.fromDt && this.toDt && Date.parse(this.toDt) < Date.parse(this.fromDt)) {
        this.$swal({ type: 'error', text: 'Created to must be on or after Created from' })
        return
      }
      this.page = 1
      if (this.showingDrafts) this.fetchDrafts()
      else this.fetchCampaigns()
      this.fetchTabCounts()
    },

    clearFilters () {
      this.searchText = ''
      this.fromDt = ''
      this.toDt = ''
      this.campaignType = ''
      this.reload()
    },

    goToPage (page) {
      this.page = page
      this.fetchCampaigns()
    },

    // Customer list and deployed journeys, for the scope bar. Same endpoints
    // createOrUpdateOpportunity.vue uses for its own dropdowns.
    async loadScopeOptions () {
      try {
        const [customerRes, journeyRes] = await Promise.all([
          api.buildRequest('getCustomerList', 'POST', {}),
          api.buildRequest('getDeployedApps', 'GET', { type: 'JOURNEY' })
        ])
        const customers = (customerRes.data && customerRes.data.payload && customerRes.data.payload.customer) || []
        this.customers = customers.map(c => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name))
        const journeys = (journeyRes.data && journeyRes.data.payload) || []
        this.journeys = journeys
          .map(j => ({ id: j.id, name: j.appName }))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)))
      } catch (err) {
        // A failed scope lookup leaves the selects on "All", which is the correct
        // unscoped behaviour — no need to block the page on it.
        this.customers = []
        this.journeys = []
      }
    },

    async fetchCampaigns () {
      this.loading = true
      const params = { ...this.buildQueryParams(this.currentTab), skip: (this.page - 1) * PAGE_SIZE }
      try {
        const { data, status } = await api.buildRequest('getOpportunityList', 'POST', params)
        if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
        const payload = (data && data.payload) || {}
        this.campaigns = payload.opportunityList || []
        this.totalCount = safeInt(payload.opportunityCount)
        this.tabCounts = { ...this.tabCounts, [this.activeTab]: this.totalCount }
        this.fetchActivity()
      } catch (err) {
        this.campaigns = []
        this.totalCount = 0
        this.$swal({ type: 'error', text: `Couldn't load campaigns: ${err.message}` })
      } finally {
        this.loading = false
      }
    },

    // Fires-to-date and last-fired for the cards on screen, in one request rather than one
    // per card. Failure is silent: the rows fall back to "—" and the page still works.
    async fetchActivity () {
      const ids = this.campaigns.map(c => c.id).filter(Boolean)
      if (!ids.length) {
        this.activity = {}
        return
      }
      try {
        const { data, status } = await api.buildRequest('nudge-campaign-activity', 'POST', { campaignIds: ids })
        if (status !== 200) return
        this.activity = (data && data.payload && data.payload.activity) || {}
      } catch (err) {
        this.activity = {}
      }
    },

    // Wizard drafts are the current user's own unfinished chat sessions. GET
    // /ai-console-chat has no per-feature column, so they're filtered client-side on the
    // shared CHAT_PURPOSE string — otherwise an Entry Point or Journey Builder chat would
    // show up here as a nudge campaign draft.
    async fetchDrafts () {
      this.loading = true
      try {
        const userId = localStorage.getItem('username') || ''
        // Every unfinished chat, not just the latest: a chat saved while its new template waits
        // to be submitted at approval has to stay resumable after the user starts another one.
        const { data, status } = await api.buildRequest('ai-console-chat', 'get', { user_id: userId })
        if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
        const payload = (data && data.payload) || []
        const list = Array.isArray(payload) ? payload : [payload]
        this.drafts = list.filter(chat => chat && (chat.purpose === CHAT_PURPOSE || chat.purpose === TEMPLATE_CHAT_PURPOSE) && isChatIncomplete(chat))
        this.tabCounts = { ...this.tabCounts, drafts: this.drafts.length }
      } catch (err) {
        this.drafts = []
        this.$swal({ type: 'error', text: `Couldn't load drafts: ${err.message}` })
      } finally {
        this.loading = false
      }
    },

    confirmDiscardDraft (draft) {
      this.$swal({
        title: 'Discard this draft?',
        text: `${this.draftName(draft)} — this can't be resumed afterward.`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Discard',
        confirmButtonColor: '#d92d20'
      }).then(result => {
        if (result && result.value) this.discardDraft(draft)
      })
    },

    async discardDraft (draft) {
      this.loading = true
      try {
        const { data, status } = await api.buildRequest(`ai-console-chat/${draft.id}`, 'PUT', { is_completed: true })
        if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
        this.$swal({ type: 'success', text: 'Draft discarded' })
      } catch (err) {
        this.$swal({ type: 'error', text: `Couldn't discard the draft: ${err.message}` })
      } finally {
        this.loading = false
      }
      this.fetchDrafts()
    },

    // One count request per campaign tab, sharing the scope and filters.
    // getOpportunityList returns the count alongside the rows, so limit: 1 keeps these
    // cheap — there's no count-only endpoint to call instead. The Drafts tab isn't in this
    // set: its count comes from the draft list itself.
    async fetchTabCounts () {
      const campaignTabs = this.tabs.filter(tab => !tab.isWizardDraft)
      const results = await Promise.all(campaignTabs.map(async tab => {
        try {
          const params = { ...this.buildQueryParams(tab), limit: 1 }
          const { data, status } = await api.buildRequest('getOpportunityList', 'POST', params)
          if (status !== 200) return [tab.key, 0]
          return [tab.key, safeInt(data && data.payload && data.payload.opportunityCount)]
        } catch (err) {
          return [tab.key, 0]
        }
      }))
      const counts = results.reduce((acc, [key, count]) => ({ ...acc, [key]: count }), {})
      this.tabCounts = { ...this.tabCounts, ...counts }
      if (!this.showingDrafts) this.refreshDraftCount()
    },

    // Keeps the Drafts badge honest while you're on another tab, without rendering them.
    async refreshDraftCount () {
      try {
        const userId = localStorage.getItem('username') || ''
        // const { data, status } = await api.buildRequest('ai-console-chat', 'GET', { user_id: userId })
        const { data, status } = await api.buildRequest('ai-console-chat', 'get', { user_id: userId })
        if (status !== 200) return
        const payload = (data && data.payload) || []
        const list = Array.isArray(payload) ? payload : [payload]
        const count = list.filter(chat => chat && (chat.purpose === CHAT_PURPOSE || chat.purpose === TEMPLATE_CHAT_PURPOSE) && isChatIncomplete(chat)).length
        this.tabCounts = { ...this.tabCounts, drafts: count }
      } catch (err) { /* badge only */ }
    },

    confirmStatusChange (campaign) {
      const action = this.statusAction(campaign)
      if (!action) return
      const gate = this.gateOf(campaign)
      this.$swal({
        title: `${action.label} this campaign?`,
        text: gate ? `${campaign.opportunity_name}. ${gate.confirm}` : campaign.opportunity_name,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: action.label
      }).then(result => {
        // A cancelled confirm leaves the checkbox visually flipped, since the DOM already
        // toggled it — refetch to put it back in step with what's stored.
        if (result && result.value) this.changeStatus(campaign, action.to)
        else this.fetchCampaigns()
      })
    },

    async changeStatus (campaign, opportunityStatus) {
      this.loading = true
      try {
        const { data, status } = await api.buildRequest('updateOpportunityStatus', 'POST', {
          id: campaign.id,
          opportunity_status: opportunityStatus
        })
        if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
        this.$swal({ type: 'success', text: (data.payload && data.payload.message) || 'Campaign updated' })
      } catch (err) {
        this.$swal({ type: 'error', text: `Couldn't update the campaign: ${err.message}` })
      } finally {
        this.loading = false
      }
      // Refetch either way — updateOpportunityStatus treats a 404 from the statistics
      // service as a success, so the card on screen isn't proof of what's stored.
      this.fetchCampaigns()
      this.fetchTabCounts()
    },

    confirmDelete (campaign) {
      this.$swal({
        title: 'Delete this campaign?',
        text: `${campaign.opportunity_name} — it stops firing and moves to the Deleted tab.`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#d92d20'
      }).then(result => {
        if (result && result.value) this.deleteCampaign(campaign)
      })
    },

    async deleteCampaign (campaign) {
      this.loading = true
      try {
        const { data, status } = await api.buildRequest('deleteOpportunity', 'POST', { id: campaign.id })
        if (status !== 200) throw new Error((data && data.payload && data.payload.message) || 'Request failed')
        this.$swal({ type: 'success', text: (data.payload && data.payload.message) || 'Campaign deleted' })
      } catch (err) {
        this.$swal({ type: 'error', text: `Couldn't delete the campaign: ${err.message}` })
      } finally {
        this.loading = false
      }
      this.fetchCampaigns()
      this.fetchTabCounts()
    },

    // Send Test — identical flow to Database Marketing's opportunity list
    // (opportunityList.vue): pick a message group, pick onboarded test users for the
    // campaign's country, POST to the same /sendTestNudge endpoint. Reuses the group's own
    // msg_template_id, so a group with no template attached (msg_template_id === null) can't
    // be test-sent — the radio option for it is disabled, same as the existing UI.
    async testNudgeClick (campaign) {
      this.selectedCampaign = campaign
      this.loading = true
      try {
        const response = await api.buildRequest('getUsersToSendTestNudges', 'POST', { countryId: campaign.country_id })
        this.userList = response.data.payload.filter(i => i.id !== 0).map(i => ({ ...i, label: i.query_name }))
        this.audienceSelectionVisible = true
        this.loading = false
      } catch (err) {
        this.audienceSelectionVisible = true
        this.loading = false
        this.selectedUsers = []
        this.selectedGroup = null
        this.$swal({ type: 'error', text: err.response ? err.response.data.payload.message : 'An error occurred' })
      }
    },

    async sendTestNudge () {
      this.submittedTestNudge = true
      this.$v.$touch()
      if (this.$v.$invalid) return
      try {
        this.audienceSelectionVisible = false
        this.loading = true
        this.submittedTestNudge = false
        this.selectedGroup.country_id = this.selectedCampaign.country_id
        this.selectedGroup.country = this.selectedCampaign.country_name
        this.selectedGroup.context = 'OE_GROUP'
        this.selectedGroup.opportunity_id = this.selectedCampaign.id
        const response = await api.buildRequest('sendTestNudge', 'POST', {
          campaign: this.selectedGroup,
          audience: this.selectedUsers
        })
        this.loading = false
        this.$swal({ type: 'success', text: response ? response.data.payload : 'Message sent successfully' })
        this.selectedUsers = []
        this.selectedGroup = null
      } catch (err) {
        this.loading = false
        this.audienceSelectionVisible = true
        this.$swal({ type: 'error', text: err.response ? err.response.data.payload.message : 'An error occurred' })
      }
    },

    closeAudienceSelection () {
      this.audienceSelectionVisible = false
      this.submittedTestNudge = false
      this.selectedUsers = []
      this.selectedGroup = null
    }
  }
}
</script>

<style scoped src="./nudge-console-theme.css"></style>
<style scoped src="./NudgeDashboard.css"></style>
<style src="./nudge-console-pager.css"></style>



// WEBPACK FOOTER //
// NudgeDashboard.vue?f9c64402